// src/components/Sections/Admin/Roles/index.jsx
//
// Roles y permisos: la pantalla desde la que la Corporación decide qué ve cada rol.
//
// Van como tarjetas y no como tabla porque lo que define a un rol no son tres columnas
// sino su matriz de permisos, que se lee en la ficha. Acá interesa otra cosa: cuántas
// personas tiene cada uno, porque es lo que dice a cuánta gente afecta el cambio que
// se está por hacer.
//
// Se separan los roles que entran al panel de los que no (cliente, funcionario): antes
// iban mezclados y "Sin acceso al panel" quedaba escondido en una línea de texto.
import React, { useCallback, useEffect, useState } from 'react';
import { Spin, Tooltip, App, Alert, Dropdown } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { FiShield, FiLock, FiUsers, FiKey, FiArrowRight, FiMoreVertical, FiPlus } from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';
import Roles from '../../../../service/Roles';
import { useNotify } from '../../../../providers/NotificationProvider.jsx';
import useAuthContext from '../../../../hooks/useAuthContext';
import { can } from '../../../../config/rbac';
import { EncabezadoAdmin, claseBtnPrimario, claseBtnSecundario } from '../../../Ui/Admin/PiezasAdmin';

/** Cuántos módulos ve y cuántos edita este rol, para resumir la matriz en la tarjeta. */
const resumir = permisos => {
  const valores = Object.values(permisos || {});
  return {
    ve: valores.filter(p => p.ver).length,
    edita: valores.filter(p => p.editar).length,
    total: valores.length,
  };
};

const TarjetaRol = ({ rol, puedeEditar, puedeBorrar, onEliminar }) => {
  const r = resumir(rol.permisos);
  const soloVe = r.ve - r.edita;
  const pct = n => (r.total ? (n / r.total) * 100 : 0);
  // Al administrador no se le tocan los permisos: es la salida de emergencia que
  // impide dejar el sistema sin nadie capaz de devolver un permiso quitado por error.
  const soloLectura = rol.key === 'admin' || !puedeEditar;

  return (
    <li className="relative">
      <Link to={`/admin/roles/${rol.key}`}
        className="group flex flex-col h-full p-5 rounded-3xl bg-white border border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,.04)] hover:border-secondary hover:shadow-[0_12px_28px_-14px_rgba(0,38,107,.25)] transition-all">
        <div className="flex items-start gap-3 pr-8">
          <span className={`w-11 h-11 flex-shrink-0 rounded-xl flex items-center justify-center transition-colors
            ${r.ve > 0 ? 'bg-blue-50 text-primary group-hover:bg-secondary group-hover:text-[#00266b]' : 'bg-gray-100 text-gray-400'}`}>
            <FiShield size={19} />
          </span>
          <div className="min-w-0">
            <h3 className="flex items-center gap-1.5 text-base font-extrabold text-gray-900 m-0">
              <span className="truncate">{rol.nombre}</span>
              {rol.sistema && (
                <Tooltip title="Rol del sistema: se le pueden editar los permisos, pero no eliminar.">
                  <FiLock size={13} className="flex-shrink-0 text-gray-400" aria-label="Rol del sistema" />
                </Tooltip>
              )}
            </h3>
            <code className="text-[11px] text-gray-400">{rol.key}</code>
          </div>
        </div>

        {rol.descripcion && <p className="text-sm text-gray-600 mt-3 mb-0 line-clamp-2">{rol.descripcion}</p>}

        <div className="mt-4 space-y-3">
          <p className="flex items-center gap-2 text-sm text-gray-700 m-0">
            <FiUsers size={14} className="text-gray-400" />
            {rol.usuarios === null || rol.usuarios === undefined
              ? <span className="text-gray-400">No se pudo contar las personas</span>
              : <><strong className="font-extrabold text-gray-900 tabular-nums">{rol.usuarios.toLocaleString('es-CL')}</strong> {rol.usuarios === 1 ? 'persona' : 'personas'}</>}
          </p>

          {r.ve > 0 ? (
            <div>
              <div className="flex h-2 rounded-full overflow-hidden bg-gray-100" aria-hidden>
                <span className="bg-primary" style={{ width: `${pct(r.edita)}%` }} />
                <span className="bg-blue-200" style={{ width: `${pct(soloVe)}%` }} />
              </div>
              <p className="flex flex-wrap gap-x-3 text-xs text-gray-500 mt-1.5 mb-0">
                <span><strong className="text-gray-900">{r.edita}</strong> edita</span>
                <span><strong className="text-gray-900">{soloVe}</strong> solo ve</span>
                <span>de {r.total} módulos</span>
              </p>
            </div>
          ) : (
            <p className="text-xs font-semibold text-gray-500 m-0">Sin acceso al panel</p>
          )}

          {rol.accesoTorniquete && (
            <Tooltip title="Quien tiene este rol abre el torniquete del recinto con su cédula, sin depender de una inscripción.">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-secondary/40 text-[#5c4300] text-xs font-bold">
                <FiKey size={12} /> Acceso al torniquete
              </span>
            </Tooltip>
          )}
        </div>

        <span className="mt-auto pt-4 inline-flex items-center gap-1.5 text-sm font-bold text-primary group-hover:gap-2.5 transition-all">
          {soloLectura ? 'Ver permisos' : 'Editar permisos'} <FiArrowRight size={14} />
        </span>
      </Link>

      {!rol.sistema && puedeBorrar && (
        <div className="absolute top-4 right-4">
          <Dropdown trigger={['click']} placement="bottomRight" menu={{
            items: [{ key: 'eliminar', danger: true, icon: <DeleteOutlined />, label: 'Eliminar rol', onClick: () => onEliminar(rol) }],
          }}>
            <button type="button" aria-label={`Más acciones de ${rol.nombre}`}
              className="w-8 h-8 inline-flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-900 transition-colors">
              <FiMoreVertical size={16} />
            </button>
          </Dropdown>
        </div>
      )}
    </li>
  );
};

const RolesList = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const { modal } = App.useApp();
  const { user } = useAuthContext();

  const [roles, setRoles] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const puedeEditar = can(user, 'roles', 'editar');
  // Borrar es solo del administrador: es lo único de esta pantalla que no se deshace
  // desde la pantalla misma.
  const puedeBorrar = user?.isAdmin === true;

  const cargar = useCallback(async () => {
    try {
      setCargando(true);
      setError(null);
      const data = await Roles.listar();
      setRoles(data.roles || []);
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'No se pudieron cargar los roles.');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const eliminar = rol => {
    modal.confirm({
      title: `¿Eliminar el rol «${rol.nombre}»?`,
      content: 'Solo se puede si ninguna persona lo tiene asignado. Esta acción no se puede deshacer.',
      okText: 'Eliminar',
      okButtonProps: { danger: true },
      cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await Roles.eliminar(rol.key);
          notify.success(`Rol «${rol.nombre}» eliminado`);
          cargar();
        } catch (e) {
          // El servidor rechaza el borrado cuando el rol está en uso y dice cuántas
          // personas lo tienen: ese mensaje es el que importa mostrar tal cual.
          notify.error(e?.response?.data?.message || 'No se pudo eliminar el rol.');
        }
      },
    });
  };

  if (cargando) {
    return <div className="py-24 text-center"><Spin size="large" /></div>;
  }

  const conPanel = roles.filter(r => resumir(r.permisos).ve > 0);
  const sinPanel = roles.filter(r => resumir(r.permisos).ve === 0);
  const personasEquipo = conPanel.reduce((acc, r) => acc + (r.usuarios || 0), 0);

  const grupo = (titulo, destacado, texto, lista) => lista.length > 0 && (
    <section className="mb-8">
      <div className="flex items-end justify-between gap-3 mb-3">
        <div>
          <h2 className="text-lg font-extrabold text-gray-900 m-0">{titulo} <span className="hl-amarillo">{destacado}</span></h2>
          <p className="text-sm text-gray-500 m-0">{texto}</p>
        </div>
        <span className="text-xs font-semibold text-gray-500">{lista.length} rol{lista.length !== 1 ? 'es' : ''}</span>
      </div>
      <ul className="m-0 p-0 list-none grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {lista.map(rol => (
          <TarjetaRol key={rol.key} rol={rol} puedeEditar={puedeEditar} puedeBorrar={puedeBorrar} onEliminar={eliminar} />
        ))}
      </ul>
    </section>
  );

  return (
    <div className="max-w-[1280px]">
      <EncabezadoAdmin
        titulo="Roles y"
        destacado="permisos"
        subtitulo={`Qué puede ver y hacer cada rol en el panel · ${personasEquipo.toLocaleString('es-CL')} personas del equipo con acceso`}
      >
        {puedeEditar && (
          <button type="button" onClick={() => navigate('/admin/roles/nuevo')} className={claseBtnPrimario}>
            <FiPlus size={16} /> Nuevo rol
          </button>
        )}
      </EncabezadoAdmin>

      {error && (
        <Alert type="error" showIcon className="mb-4" message={error}
          action={<button type="button" onClick={cargar} className={claseBtnSecundario}>Reintentar</button>} />
      )}

      {!roles.length && !error ? (
        <div className="py-16 text-center rounded-3xl bg-white border border-gray-100">
          <p className="font-bold text-gray-900 m-0">No hay roles configurados</p>
        </div>
      ) : (
        <>
          {grupo('Con acceso', 'al panel', 'El equipo de la Corporación: entran al panel según su matriz de permisos.', conPanel)}
          {grupo('Sin acceso', 'al panel', 'Vecinos y funcionarios: usan el sitio público o el torniquete, no el panel.', sinPanel)}
        </>
      )}
    </div>
  );
};

export default RolesList;
